import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SellnoModalComponent } from '../../../shared/components/sellno-modal/sellno-modal.component';
import { DepositModalComponent } from '../../../shared/components/deposit-modal/deposit-modal.component';
import { USER_PROFIT_SNAPSHOT } from '../../../common/interface/request.interface';
import { PortfolioTab, ActivityType, PositionStatus, OrderHistoryType, OrderSort, PositionSort, POSITION_SORT_DROPDOWN_OPTIONS } from '../../../common/constants/common.constants';
import { ToastService } from '../../../services/toast.service';
import { WalletService } from '../../../services/wallet.service';
import { Web2ServiceService } from '../../../services/web2-service.service';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ChartComponent } from '../chart/chart.component';
import { AmountFormatDirective } from '../../../shared/directives/amount-format.directive';
import { LoaderService } from '../../../services/loader.service';


const ActivityStyle: Record<ActivityType, { color: string; symbol: string }> = {
  [ActivityType.SOLD]: { color: 'text-green-600', symbol: '+' },
  [ActivityType.CLAIMED]: { color: 'text-green-600', symbol: '+' },
  [ActivityType.DEPOSIT]: { color: 'text-green-600', symbol: '+' },
  [ActivityType.DEPOSITED]: { color: 'text-green-600', symbol: '+' },
  [ActivityType.LOST]: { color: 'text-red-600', symbol: '-' },
  [ActivityType.BOUGHT]: { color: 'text-black', symbol: '-' },
  [ActivityType.WITHDREW]: { color: 'text-red-600', symbol: '-' },
};

type WithdrawDropdownOption = {
  label: string;
  iconClass: string;
};

@Component({
  selector: 'app-portfolio',
  imports: [NavBarComponent, CommonModule, SellnoModalComponent, DepositModalComponent, FormsModule, ChartComponent, AmountFormatDirective],
  templateUrl: './portfolio.component.html',
  styleUrl: './portfolio.component.css'
})
export class PortfolioComponent implements OnInit {
  @ViewChild('withdrawRecipientField') withdrawRecipientField?: ElementRef<HTMLInputElement>;
  categoryList: Array<any> = [];
  activeTab: PortfolioTab = PortfolioTab.POSITIONS;
  PortfolioTab = PortfolioTab;
  orderLifecycle: PositionStatus = PositionStatus.OPEN;
  PositionStatus = PositionStatus;
  selectedValue = 'Alphabetically';
  selectedSortBy: PositionSort = PositionSort.MARKET_NAME;
  PositionSort = PositionSort;
  positionSortOptions = POSITION_SORT_DROPDOWN_OPTIONS;


  isValueDropdownOpen = false;
  isSortDropdownOpen = false;
  selectedOrderSort: OrderSort = OrderSort.MARKET;
  OrderSort = OrderSort;
  marketOptions = [OrderSort.MARKET, OrderSort.FILL_QUANTITY, OrderSort.TOTAL_QUANTITY, OrderSort.ORDER_DATE, OrderSort.RESOLVING_SOONEST];
  isSellNoModalOpen = false;
  selectedSellPosition: any = null;
  isDepositModalOpen = false;
  isWithdrawModalOpen = false;
  claimDetails: any[] = [];
  isProcessing: boolean = false;
  isAllOpen: boolean = false;
  allSelected: OrderHistoryType = OrderHistoryType.ALL;
  allOptions: OrderHistoryType[] = Object.values(OrderHistoryType);

  isNewestOpen: boolean = false;
  newestSelected: string = 'Newest';
  newestOptions: string[] = ['Newest', 'Oldest', 'Value', 'Shares'];

  positions: any[] = [];
  orders: any[] = [];

  // Pagination properties
  private readonly PAGE_SIZE = 25;

  // Positions pagination
  positionsPage: number = 1;
  hasMorePositions: boolean = true;
  isLoadingPositions: boolean = false;

  // Orders pagination
  ordersPage: number = 1;
  hasMoreOrders: boolean = true;
  isLoadingOrders: boolean = false;

  // History pagination
  historyPage: number = 1;
  hasMoreHistory: boolean = true;
  isLoadingHistory: boolean = false;

  // Positions search
  searchQuery: string = '';
  searchTimeout: any;

  // Orders search
  ordersSearchQuery: string = '';
  ordersSearchTimeout: any;

  // history search
  historysearchQuery: string = '';
  historySearchTimeout: any;
  isDatePickerOpen: boolean = false;
  historyStartDate: string | null = null;
  historyEndDate: string | null = null;

  historys: any[] = [];
  profitSnapshot: USER_PROFIT_SNAPSHOT | null = null;
  profitHistoryData: any[] = [];
  activeProfitInterval: string = 'ALL';
  currentProfitLossValue: string = '0.00';
  availableCashBalance: number = 0;
  connectedWalletAddress = '';
  withdrawRecipient = '';
  withdrawAmount = '';
  readonly withdrawTokenOptions: WithdrawDropdownOption[] = [
    { label: 'USDC', iconClass: 'withdraw-option-icon withdraw-select-token' }
  ];
  readonly withdrawChainOptions: WithdrawDropdownOption[] = [
    { label: 'Polygon', iconClass: 'withdraw-option-icon withdraw-select-chain' }
  ];
  selectedWithdrawToken = this.withdrawTokenOptions[0];
  selectedWithdrawChain = this.withdrawChainOptions[0];
  isWithdrawTokenDropdownOpen = false;
  isWithdrawChainDropdownOpen = false;


  constructor(
    private elementRef: ElementRef,
    private service: ApiService,
    private auth: AuthService,
    private toastr: ToastService,
    public walletService: WalletService,
    private web2Service: Web2ServiceService,
    private router: Router,
    private loader: LoaderService
  ) { }


  private portfolioGlobalSubscription: any;

  async ngOnInit() {
    // Start global loader at the very beginning of component initialization
    this.loader.show();

    try {
      // Coordinate all critical data fetching: wait for every API to finish before hiding the loader
      // This prevents the "blank state" or "ghosting" on page refresh
      await Promise.all([
        this.getCategories(),
        this.loadPositions(),
        this.loadProfitHistory(this.activeProfitInterval),
        this.loadProfitSnapshot()
      ]);

      // Ensure tab-specific data is also ready if relevant
      if (this.activeTab === PortfolioTab.ORDERS) {
        await this.loadOrders();
      } else if (this.activeTab === PortfolioTab.HISTORY) {
        await this.loadHistory();
      }
    } catch (error) {
      console.error("Error during portfolio initialization:", error);
    } finally {
      // Hide loader only when all concurrent tasks have completed (success or error)
      this.loader.hide();
    }

    // Subscribe to the global stream managed by AuthService
    this.portfolioGlobalSubscription = this.auth.portfolioSummary$.subscribe({
      next: (res: any) => {
        if (!res) {
          // The stream starts with a null placeholder. Do not clear tab data on
          // page refresh, otherwise the initial positions API result is wiped.
          if (!this.auth.getSessionToken()) {
            this.profitSnapshot = null;
            this.availableCashBalance = 0;
            this.claimDetails = [];
            this.positions = [];
            this.orders = [];
            this.historys = [];
          }
          return;
        }
        // console.log("Portfolio UI Update from Global Stream:", res);
        // Custom mapping to match your template fields
        this.profitSnapshot = {
          total_profit: res.portfolio_value,                 // Bold header
          portfolio_value: res.available_to_trade,           // Icon amount
          today_profit: res.change_24h,                      // performance value
          percent_change: res.percentage_change_24h           // performance percentage
        } as USER_PROFIT_SNAPSHOT;
        this.availableCashBalance = Number(res.available_to_trade ?? res.data?.available_to_trade ?? 0);

        if (res.claim_details && res.claim_details.length > 0) {
          this.claimDetails = (res.claim_details || []).map((item: any) => ({
            ...item,
            claimable_amount: Number(item.claimable_amount ?? item.amount ?? 0),
            isProcessing: false
          }));
        }
      }
    });
  }

  navigateToMarket(marketId: string | undefined): void {
    // console.log("Navigating to market with ID:", marketId);
    if (marketId) {
      this.router.navigate(['/details', marketId]);
    } else {
      console.warn("Market ID is undefined or missing!");
    }
  }

  formatCurrency(value: string | undefined, showPlus: boolean = false): string {
    if (!value) return '$0.00';
    // Remove any existing '$' or '+' if they were hardcoded before
    const cleanValue = value.replace('$', '').replace('+', '');
    if (cleanValue.startsWith('-')) {
      return '-$' + cleanValue.slice(1);
    }
    return (showPlus ? '+' : '') + '$' + cleanValue;
  }

  get profitChartData() {
    return {
      yes: this.profitHistoryData,
      no: []
    };
  }

  get withdrawAmountNumber(): number {
    return Number(this.withdrawAmount || 0);
  }

  get estimatedReceiveAmount(): string {
    return this.withdrawAmountNumber > 0 ? this.withdrawAmountNumber.toFixed(2) : '';
  }

  get withdrawToken(): string {
    return this.selectedWithdrawToken.label;
  }

  get withdrawChain(): string {
    return this.selectedWithdrawChain.label;
  }

  get withdrawUsdValue(): string {
    return this.estimatedReceiveAmount;
  }

  get withdrawFeePercentage(): string {
    return '0.00%';
  }

  get isWithdrawFormValid(): boolean {
    return !!this.withdrawRecipient.trim()
      && this.walletService.isValidAddress(this.withdrawRecipient.trim())
      && this.withdrawAmountNumber > 0
      && this.hasValidWithdrawPrecision(this.withdrawAmount)
      && this.withdrawAmountNumber <= this.availableCashBalance;
  }

  hasValidWithdrawPrecision(value: string): boolean {
    return /^\d+(\.\d{1,2})?$/.test((value || '').trim());
  }

  ngOnDestroy() {
    if (this.portfolioGlobalSubscription) {
      this.portfolioGlobalSubscription.unsubscribe();
    }
  }

  toggleDropdown() {
    this.isValueDropdownOpen = !this.isValueDropdownOpen;
  }

  selectValue(option: { label: string, value: PositionSort }) {
    this.selectedValue = option.label;
    this.selectedSortBy = option.value;
    this.isValueDropdownOpen = false;
    this.loadPositions();
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: Event) {
    const path = event.composedPath();

    // Check if the click was inside any dropdown specifically
    const isInsideCurrentValue = path.some(el => el instanceof HTMLElement && el.classList.contains('current-value-dropdown'));
    const isInsideWithdrawSelect = path.some(el => el instanceof HTMLElement && el.classList.contains('withdraw-select-dropdown'));

    // If we didn't click inside the general dropdown area, close everything
    if (!isInsideCurrentValue && !isInsideWithdrawSelect) {
      this.isAllOpen = false;
      this.isNewestOpen = false;
      this.isValueDropdownOpen = false;
      this.isSortDropdownOpen = false;
      this.isWithdrawTokenDropdownOpen = false;
      this.isWithdrawChainDropdownOpen = false;
      this.isDatePickerOpen = false;
    }
  }

  openSellNoModal(item?: any) {
    this.selectedSellPosition = item || null;
    this.isSellNoModalOpen = true;
  }

  closeSellNoModal() {
    this.isSellNoModalOpen = false;
    this.selectedSellPosition = null;
  }

  onCashoutSuccess() {
    this.closeSellNoModal();
    // Refresh positions to remove the sold one
    this.loadPositions();
    // Refresh balance/profit
    this.loadProfitSnapshot();
  }

  onEditOrder(item?: any) {
    const pos = item || this.selectedSellPosition;
    if (!pos) return;
    this.closeSellNoModal();
    this.router.navigate(['/details', pos.market_id], {
      queryParams: {
        action: 'Sell',
        outcome: pos.team?.toLowerCase(),
        orderType: 'Market',
        shares: pos.rawShares
      }
    });
  }

  openDepositModal() {
    this.isDepositModalOpen = true;
  }

  closeDepositModal() {
    this.isDepositModalOpen = false;
  }

  openWithdrawModal() {
    this.connectedWalletAddress = this.walletService.getAddress() || this.auth.getUserData()?.public_address || '';
    // If not restoring from persistent state, reset fields
    if (!this.isWithdrawModalOpen) {
      this.withdrawRecipient = '';
      this.withdrawAmount = '';
    }
    this.isWithdrawModalOpen = true;
    // Force a balance refresh when opening modal
    this.auth.reUpdateProfile(true);
  }

  closeWithdrawModal() {
    this.isWithdrawModalOpen = false;
    this.withdrawRecipient = '';
    this.withdrawAmount = '';
    this.selectedWithdrawToken = this.withdrawTokenOptions[0];
    this.selectedWithdrawChain = this.withdrawChainOptions[0];
    this.isWithdrawTokenDropdownOpen = false;
    this.isWithdrawChainDropdownOpen = false;
  }

  setWithdrawMax() {
    this.withdrawAmount = this.availableCashBalance > 0 ? this.availableCashBalance.toFixed(2) : '0.00';
  }

  useConnectedWalletAddress() {
    this.withdrawRecipient = this.connectedWalletAddress;
    // Trigger resize after model update
    setTimeout(() => {
      if (this.withdrawRecipientField) {
        this.autoResizeWithdrawRecipient(this.withdrawRecipientField.nativeElement as any);
      }
    });
  }

  autoResizeWithdrawRecipient(field: HTMLTextAreaElement) {
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = field.scrollHeight + 'px';
  }

  toggleWithdrawTokenDropdown(event: Event) {
    event.stopPropagation();
    this.isWithdrawTokenDropdownOpen = !this.isWithdrawTokenDropdownOpen;
    if (this.isWithdrawTokenDropdownOpen) {
      this.isWithdrawChainDropdownOpen = false;
    }
  }

  selectWithdrawToken(option: WithdrawDropdownOption) {
    this.selectedWithdrawToken = option;
    this.isWithdrawTokenDropdownOpen = false;
  }

  toggleWithdrawChainDropdown(event: Event) {
    event.stopPropagation();
    this.isWithdrawChainDropdownOpen = !this.isWithdrawChainDropdownOpen;
    if (this.isWithdrawChainDropdownOpen) {
      this.isWithdrawTokenDropdownOpen = false;
    }
  }

  selectWithdrawChain(option: WithdrawDropdownOption) {
    this.selectedWithdrawChain = option;
    this.isWithdrawChainDropdownOpen = false;
  }

  setTab(tab: PortfolioTab) {
    this.activeTab = tab;
    if (tab === PortfolioTab.POSITIONS) {
      this.loadPositions();
    } else if (tab === PortfolioTab.ORDERS) {
      this.loadOrders();
    } else if (tab === PortfolioTab.HISTORY) {
      this.loadHistory();
    }
  }
  loadOrders(isLoadMore: boolean = false) {
    const userData = this.auth.getUserData();
    const userId = userData?.id;

    if (!userId) {
      console.warn("No user ID available, cannot load orders");
      return;
    }

    // Reset pagination when doing a fresh search
    if (!isLoadMore) {
      this.ordersPage = 1;
      this.orders = [];
      this.hasMoreOrders = true;
    }

    if (this.isLoadingOrders || !this.hasMoreOrders) {
      return;
    }

    this.isLoadingOrders = true;
    const searchParam = this.ordersSearchQuery?.trim() || null;

    this.service.getMarketOpenOrders(null, userId, searchParam, this.orderLifecycle, this.ordersPage, this.PAGE_SIZE).subscribe({
      next: (res) => {
        const newOrders = res.data.results.map((order: any) => ({
          ...order,
          market_id: order.market_id || order.market?.id || order.id,
          market_image: order.market_image_url || order.market?.image_url,
          outcome: order.outcome_name,
          filled: order.filled_quantity,
          total: order.total_quantity,
          total_amount: order.total_amount,
          expiration: 'Until Cancelled'
        }));

        if (!isLoadMore) {
          this.orders = newOrders;
          this.sortOrders();
        } else {
          this.orders = [...this.orders, ...newOrders];
          this.sortOrders();
        }

        // Check if there are more records
        this.hasMoreOrders = newOrders.length === this.PAGE_SIZE;
        this.ordersPage++;
        this.isLoadingOrders = false;
      },
      error: (err) => {
        console.error("Error loading portfolio orders:", err);
        this.isLoadingOrders = false;
      }
    });
  }

  setOrderLifecycle(lifecycle: PositionStatus) {
    this.orderLifecycle = lifecycle;
    this.loadOrders();
  }

  selectOrderSort(option: OrderSort) {
    this.selectedOrderSort = option;
    this.isSortDropdownOpen = false;
    this.sortOrders();
  }

  private sortOrders() {
    if (!this.orders || this.orders.length === 0) return;

    switch (this.selectedOrderSort) {
      case OrderSort.MARKET:
        this.orders.sort((a, b) => (a.market_name || '').localeCompare(b.market_name || ''));
        break;
      case OrderSort.FILL_QUANTITY:
        this.orders.sort((a, b) => (b.filled || 0) - (a.filled || 0));
        break;
      case OrderSort.TOTAL_QUANTITY:
        this.orders.sort((a, b) => (b.total || 0) - (a.total || 0));
        break;
      case OrderSort.ORDER_DATE:
        this.orders.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        break;
      case OrderSort.RESOLVING_SOONEST:
        this.orders.sort((a, b) => {
          const dateA = new Date(a.market?.end_date || a.end_date || 0).getTime();
          const dateB = new Date(b.market?.end_date || b.end_date || 0).getTime();
          return dateA - dateB;
        });
        break;
    }
  }

  loadHistory(isLoadMore: boolean = false) {
    const userData = this.auth.getUserData();
    const userId = userData?.id;
    let shares = '';
    let price = '';
    if (!userId) return;

    // Reset pagination when doing a fresh search
    if (!isLoadMore) {
      this.historyPage = 1;
      this.historys = [];
      this.hasMoreHistory = true;
    }

    if (this.isLoadingHistory || !this.hasMoreHistory) {
      return;
    }

    this.isLoadingHistory = true;
    const searchParam = this.historysearchQuery?.trim() || null;
    const start = this.historyStartDate;
    const end = this.historyEndDate;

    const orderType = (this.allSelected || 'ALL').toUpperCase();
    const orderDirection = (this.newestSelected || 'NEWEST').toUpperCase();

    this.service.getUserOrderHistory(userId, searchParam, start, end, orderType, orderDirection, this.historyPage, this.PAGE_SIZE).subscribe({
      next: (res: any) => {
        const newHistorys = res.data.results.map((item: any) => {
          const activityWord = item.activity || 'Activity';
          let marketName = item.market_name || '';
          let marketImage = item.market_image_url || '';
          let marketId = item.market.market_id || item.id || '';

          if (typeof item.market === 'object' && item.market) {
            marketName = marketName || item.market.name || '';
            marketImage = marketImage || item.market.image_url || '';
            marketId = marketId || item.market.id || item.market.market_id || '';
            shares = item.market.shares;
            price = item.market.price;
          } else if (typeof item.market === 'string' && !marketName) {
            marketName = item.market;
          }

          return {
            ...item,
            displayActivity: activityWord,
            displayActivityFull: activityWord,
            displayMarket: {
              name: marketName,
              image: marketImage,
              id: marketId
            },
            total_execution: item.total_execution,
            shares: item.shares,
            price: item.price,
            time_ago: item.time_ago
          };
        });

        if (isLoadMore) {
          this.historys = [...this.historys, ...newHistorys];
        } else {
          this.historys = newHistorys;
        }

        // Check if there are more records
        this.hasMoreHistory = newHistorys.length === this.PAGE_SIZE;
        this.historyPage++;
        this.isLoadingHistory = false;
        console.log("Mapped History:", this.historys);
      },
      error: (err) => {
        console.error(err);
        this.isLoadingHistory = false;
      }
    });
  }

  isHistoryMarketClickable(history: any): boolean {
    const activity = (history?.activity || '').toString().toLowerCase();
    const hasMarketId = !!history?.market?.market_id;
    const isTradeActivity = activity.includes('bought') || activity.includes('sold');
    return hasMarketId && isTradeActivity;
  }

  toggleDatePicker() {
    this.isDatePickerOpen = !this.isDatePickerOpen;
    if (this.isDatePickerOpen) {
      this.isAllOpen = false;
      this.isNewestOpen = false;
    }
  }

  onDateChange() {
    // This can be used to auto-load if both dates are set or on manual Apply
    if (this.historyStartDate && this.historyEndDate) {
      this.loadHistory();
    }
  }

  clearDates() {
    this.historyStartDate = null;
    this.historyEndDate = null;
    this.isDatePickerOpen = false;
    this.loadHistory();
  }

  applyDateFilter() {
    this.isDatePickerOpen = false;
    this.loadHistory();
  }

  setHistoryAll(option: OrderHistoryType) {
    this.allSelected = option;
    this.isAllOpen = false;
    this.loadHistory();
  }

  setHistoryNewest(option: string) {
    this.newestSelected = option;
    this.isNewestOpen = false;
    this.loadHistory();
  }

  exportOrderHistory() {
    const userId = this.auth.getUserData()?.id;
    if (!userId) {
      this.toastr.error('User ID not found');
      return;
    }

    const searchParam = this.historysearchQuery?.trim() || null;
    const start = this.historyStartDate;
    const end = this.historyEndDate;
    const orderType = (this.allSelected || OrderHistoryType.ALL).toUpperCase();
    const orderDirection = (this.newestSelected || 'NEWEST').toUpperCase();

    this.service.exportUserOrderHistory(userId, searchParam, start, end, orderType, orderDirection).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `order-history-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Order history exported successfully');
      },
      error: (err) => {
        console.error('Export failed:', err);
        this.toastr.error('Failed to export order history');
      }
    });
  }

  getActivityClass(activity: string | undefined): string {
    if (!activity) return 'text-black';

    const act = activity.toLowerCase() as ActivityType;

    if (ActivityStyle[act]) {
      return ActivityStyle[act].color;
    }

    return 'text-black';
  }

  getActivitySymbol(activity: string | undefined): string {
    if (!activity) return '';

    const act = activity.toLowerCase() as ActivityType;

    if (ActivityStyle[act]) {
      return ActivityStyle[act].symbol;
    }

    return '-';
  }

  formatPrice(value: any): string {
    if (value === null || value === undefined) return '0¢';

    const num = Number(value);

    if (isNaN(num)) return '0¢';

    // Case 1: already in 0–1 range (0.72 → 72¢)
    if (num > 0 && num <= 1) {
      return Math.round(num * 100) + '¢';
    }

    // Case 2: already in cents (72 → 72¢)
    if (num > 1 && num <= 100) {
      return Math.round(num) + '¢';
    }

    // Case 3: large blockchain number
    return Math.round(num / 1000000) + '¢';
  }

  getCategories(): Promise<void> {
    return new Promise((resolve) => {
      this.service.getCategoriesList().subscribe({
        next: (result: any) => {
          this.categoryList = result.data || [];
          resolve();
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          resolve();
        }
      });
    });
  }

  loadPositions(isLoadMore: boolean = false): Promise<void> {
    return new Promise((resolve) => {
      // console.log("loadPositions called, isLoadMore:", isLoadMore);
      const userData = this.auth.getUserData();
      const userId = userData?.id;
      //console.log("User ID for positions:", userId);

      if (!userId) {
        console.warn("No user ID available, cannot load positions");
        resolve();
        return;
      }

      // Reset pagination when doing a fresh search or initial load
      if (!isLoadMore) {
        this.positionsPage = 1;
        this.positions = [];
        this.hasMorePositions = true;
      }

      if (this.isLoadingPositions || !this.hasMorePositions) {
        resolve();
        return;
      }

      this.isLoadingPositions = true;
      const searchParam = this.searchQuery?.trim() || null;

      this.service.getUserPositions(userId, searchParam, this.selectedSortBy, this.positionsPage, this.PAGE_SIZE).subscribe({
        next: (res) => {
          const newPositions = res.data.results.map((pos: any) => {
            const value = parseFloat(pos.value || '0');
            const bet = parseFloat(pos.amount_bet || '0');
            const totalPnlValue = parseFloat(pos.total_pnl ?? pos.profit_loss ?? '0');
            const pnlPercentValue = parseFloat(pos.profit_loss_pct ?? '0');
            return {
              market_id: pos.market_id || pos.market?.id || pos.id,
              image: pos.market_image_url || pos.market?.image_url,
              question: pos.market_name || pos.market?.name,
              team: pos.outcome_title,
              sharesText: pos.shares + ' Shares',
              avg: this.formatPrice(pos.avg_price),
              now: this.formatPrice(pos.current_price),
              bet: '$' + bet.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              toWin: '$' + parseFloat(pos.payout_when_win || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              value: '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              isProfit: totalPnlValue >= 0,
              pnlValue: (totalPnlValue >= 0 ? '+$' : '-$') + Math.abs(totalPnlValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              pnlPercent: (Number.isFinite(pnlPercentValue) ? pnlPercentValue : 0).toFixed(2) + '%',
              resolution_state: pos.resolution_state || pos.market?.resolution_state,
              is_winner_declared: pos.is_winner_declared || pos.market?.is_winner_declared,
              is_winner: pos.is_winner,
              isLost: (pos.is_winner_declared || pos.market?.is_winner_declared) && pos.is_winner === false,
              isWon: (pos.is_winner_declared || pos.market?.is_winner_declared) && pos.is_winner === true,
              outcome_id: pos.outcome_id || pos.outcome?.id,
              totalPnl: (totalPnlValue >= 0 ? '+' : '-') + '$' + Math.abs(totalPnlValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              rawShares: pos.shares,
              rawCurrentPrice: pos.current_price,
              rawValue: value
            };
          });

          if (isLoadMore) {
            this.positions = [...this.positions, ...newPositions];
          } else {
            this.positions = newPositions;
          }

          // Check if there are more records
          this.hasMorePositions = newPositions.length === this.PAGE_SIZE;
          this.positionsPage++;
          this.isLoadingPositions = false;
          resolve();
        },
        error: (err) => {
          console.error("Error loading portfolio positions:", err);
          this.isLoadingPositions = false;
          resolve();
        }
      });
    });
  }

  onWithdrawAmountChange() {
  }

  onWithdrawRecipientChange() {
  }

  displayTags(): void {

  }

  onSearchPositions(event: any) {
    this.searchQuery = event.target.value;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.loadPositions();
    }, 500);
  }

  onSearchOrders(event: any) {
    this.ordersSearchQuery = event.target.value;
    if (this.ordersSearchTimeout) {
      clearTimeout(this.ordersSearchTimeout);
    }
    this.ordersSearchTimeout = setTimeout(() => {
      this.loadOrders();
    }, 500);
  }

  onSearchHistory(event: any) {
    this.historysearchQuery = event.target.value;
    if (this.historySearchTimeout) {
      clearTimeout(this.historySearchTimeout);
    }
    this.historySearchTimeout = setTimeout(() => {
      this.loadHistory();
    }, 500);
  }

  // Scroll-based pagination handlers
  onPositionsScroll(event: any): void {
    const element = event.target;
    const scrollThreshold = 200; // pixels from bottom

    // Calculate if we're near the bottom
    const isNearBottom = element.scrollHeight - (element.scrollTop + element.clientHeight) < scrollThreshold;

    if (isNearBottom && !this.isLoadingPositions && this.hasMorePositions) {
      this.loadPositions(true);
    }
  }

  onOrdersScroll(event: any): void {
    const element = event.target;
    const scrollThreshold = 200; // pixels from bottom

    // Calculate if we're near the bottom
    const isNearBottom = element.scrollHeight - (element.scrollTop + element.clientHeight) < scrollThreshold;

    if (isNearBottom && !this.isLoadingOrders && this.hasMoreOrders) {
      this.loadOrders(true);
    }
  }

  onHistoryScroll(event: any): void {
    const element = event.target;
    const scrollThreshold = 200; // pixels from bottom

    // Calculate if we're near the bottom
    const isNearBottom = element.scrollHeight - (element.scrollTop + element.clientHeight) < scrollThreshold;

    if (isNearBottom && !this.isLoadingHistory && this.hasMoreHistory) {
      this.loadHistory(true);
    }
  }

  get hasClaimable(): boolean {
    return this.claimDetails?.some(item => item.claimable_amount > 0) ?? false;
  }
  /**
   * Fetches the user's overall profit snapshot (Total Profit, Today's Profit, etc.)
   * Used in the summary header of the Portfolio page.
   */
  // --------------------- Load Profit Snapshot ---------------------
  loadProfitSnapshot(): Promise<void> {
    return new Promise((resolve) => {
      const userData = this.auth.getUserData();
      const userId = userData?.id;
      if (!userId) {
        resolve();
        return;
      }

      this.service.getUserProfitSnapshot(userId).subscribe({
        next: (res: any) => {
          this.claimDetails = (res.data?.claim_details || []).map((item: any) => ({
            ...item,
            claimable_amount: Number(item.claimable_amount ?? item.amount ?? 0),
            isProcessing: false
          }));
          resolve();
        },
        error: (err) => {
          console.error('Error loading profit snapshot:', err);
          resolve();
        }
      });
    });
  }

  // --------------------- Redeem Claim ---------------------
  async onRedeem(item: any) {
    if (item.isProcessing) return;

    // 1. Declare and check userId here
    const userData = this.auth.getUserData();
    const userId = userData?.id;

    if (!userId) {
      this.toastr.error("User not authenticated");
      return;
    }

    item.isProcessing = true;

    try {
      // 2. Now userId is "visible" to this block
      const response: any = await firstValueFrom(
        this.service.prepareRedeem(item.market_id, userId, item.outcome_id)
      );

      // Check if the response structure is nested (based on the JSON you showed)
      const prepareRes = response.data;

      if (!prepareRes || !prepareRes.tx_hash) {
        throw new Error("Transaction hash missing from API response");
      }

      // 3. Sign using the hash from the prepareRes object — branch on user type
      let rawSignature: string;

      if (userData.user_type?.toUpperCase() === 'WEB2') {
        // Web2: sign via Magic's rpcProvider, not Wagmi
        const meta = await this.web2Service.getUserMetaData();
        const signingAddress = meta?.publicAddress || userData.public_address;
        if (!signingAddress) throw new Error('No valid signing address for Web2 user');
        rawSignature = await this.web2Service.signRawHash(signingAddress, prepareRes.tx_hash);
      } else {
        // Web3: sign via Wagmi
        rawSignature = await this.walletService.signGnosisSafeHash(prepareRes.tx_hash);
      }
      // 4. Format signature for Gnosis Safe (normalise v, strip 0x)
      let signature: string;

      if (userData.user_type?.toUpperCase() === 'WEB2') {
        // Web2: personal_sign output needs v-normalization via formatGnosisSafeSignature
        const formatted = this.walletService.formatGnosisSafeSignature(rawSignature);
        signature = formatted.startsWith('0x') ? formatted.slice(2) : formatted;
      } else {
        // Web3: signGnosisSafeHash already formats; just strip 0x
        if (typeof rawSignature === 'string') {
          signature = rawSignature.startsWith('0x') ? rawSignature.slice(2) : rawSignature;
        } else {
          signature = Array.from(rawSignature as Uint8Array)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
        }
      }
      // 5. Execute
      const executeRes: any = await firstValueFrom(
        this.service.executeRedeem(item.market_id, userId, {
          ...prepareRes,
          signature: signature
        }, item.outcome_id)
      );

      if (executeRes?.data?.redeemed) {
        this.toastr.success(`Claimed $${executeRes.data.redeemed_amount}`);
        this.loadProfitSnapshot();
        this.loadPositions();
      }

    } catch (err: any) {
      console.error('Redeem failed:', err);
      this.toastr.error(err?.code === 4001 ? 'Signature rejected' : 'Claim failed');
    } finally {
      item.isProcessing = false;
    }
  }
  /**
   * Fetches the user's profit history time-series data for the selected interval.
   * This data is used to populate the Profit/Loss chart.
   * Currently, the chart is using a static image for UI placeholder, 
   * but the dynamic data is available via `profitHistoryData`.
   */
  loadProfitHistory(interval: string): Promise<void> {
    return new Promise((resolve) => {
      const userData = this.auth.getUserData();
      const userId = userData?.id;
      this.activeProfitInterval = interval;

      if (!userId) {
        console.warn("No user ID available, cannot load profit history");
        resolve();
        return;
      }

      this.service.getUserProfitHistory(userId, interval).subscribe({
        next: (res: any) => {

          const rawPoints = (res.data.points || []).map((p: any) => ({
            time: Math.floor(new Date(p.timestamp).getTime() / 1000),
            value: parseFloat(p.profit || 0)
          })).sort((a: any, b: any) => a.time - b.time);

          if (rawPoints.length === 0) {
            const now = Math.floor(Date.now() / 1000);

            this.profitHistoryData = [
              { time: now - 86400, value: 0 },
              { time: now, value: 0 }
            ];

            const totalProfitNum = parseFloat(res?.data?.total_profit ?? 0);
            const prefix = totalProfitNum >= 0 ? '+' : '-';
            this.currentProfitLossValue =
              `${prefix}$${Math.abs(totalProfitNum).toFixed(2)}`;

            resolve();
            return;
          }

          // 👉 normal flow (NON-empty data)
          const firstTime = rawPoints[0].time;

          this.profitHistoryData = [
            { time: firstTime - 86400, value: 0 },
            ...rawPoints
          ];

          const totalProfitNum = parseFloat(res?.data?.total_profit ?? 0);
          const prefix = totalProfitNum >= 0 ? '+' : '-';
          this.currentProfitLossValue =
            `${prefix}$${Math.abs(totalProfitNum).toFixed(2)}`;
          resolve();
        },

        error: (err) => {
          console.error("Error loading profit history:", err);
          resolve();
        }
      });
    });
  }

  setProfitInterval(interval: string) {
    this.loadProfitHistory(interval);
  }

  async handleWithdraw() {
    if (!this.isWithdrawFormValid || this.isProcessing) return;

    const userData = this.auth.getUserData();
    const userId = userData?.id;

    if (!userId) {
      this.toastr.error("User not authenticated");
      return;
    }

    this.isProcessing = true;
    let externalWalletAddress: string | undefined = this.withdrawRecipient.trim();

    // fallback
    if (!externalWalletAddress) {
      externalWalletAddress = this.walletService.getAddress();
    }

    const amount = this.withdrawAmountNumber;

    // final validation
    if (!externalWalletAddress) {
      this.toastr.error('Please enter wallet address');
      this.isProcessing = false;
      return;
    }

    if (!this.walletService.isValidAddress(externalWalletAddress)) {
      this.toastr.error('Please enter a valid wallet address');
      this.isProcessing = false;
      return;
    }

    if (amount <= 0) {
      this.toastr.error('Please enter a valid amount');
      this.isProcessing = false;
      return;
    }

    if (amount > this.availableCashBalance) {
      this.toastr.error('Insufficient balance');
      this.isProcessing = false;
      return;
    }

    try {
      // 1. Prepare Withdraw
      const prepareRes: any = await firstValueFrom(
        this.service.prepareWithdraw(userId, {
          amount,
          external_wallet_address: externalWalletAddress
        })
      );

      if (!prepareRes?.data?.safe_tx_hash) {
        throw new Error("Safe transaction hash missing from preparation response");
      }

      let safeTxHash = prepareRes.data.safe_tx_hash;
      if (!safeTxHash.startsWith('0x')) {
        safeTxHash = '0x' + safeTxHash;
      }

      // 2. Sign the hash — branch on user type (Web2 = Magic, Web3 = Wagmi)
      let signature: string;

      if (userData.user_type?.toUpperCase() === 'WEB2') {
        // Web2 users have no Wagmi connector — sign via Magic's rpcProvider
        const meta = await this.web2Service.getUserMetaData();
        const signingAddress = meta?.publicAddress || userData.public_address;
        if (!signingAddress) throw new Error('No valid signing address for Web2 user');

        const rawSignature = await this.web2Service.signRawHash(signingAddress, safeTxHash);
        signature = this.walletService.formatGnosisSafeSignature(rawSignature);
        signature = signature.startsWith('0x') ? signature.slice(2) : signature;
      } else {
        // Web3 users sign via Wagmi
        const rawSignature = await this.walletService.signGnosisSafeHash(safeTxHash);
        if (typeof rawSignature === 'string') {
          signature = rawSignature.startsWith('0x') ? rawSignature.slice(2) : rawSignature;
        } else {
          signature = Array.from(rawSignature as Uint8Array)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
        }
      }

      // 3. Execute Withdraw
      const executePayload = {
        amount: amount,
        tx_hash: safeTxHash,
        signature: signature,
        fee: 0, // Default for now as per schema
        net_amount: amount,
        external_wallet_address: externalWalletAddress

      };

      const executeRes: any = await firstValueFrom(
        this.service.executeWithdraw(userId, executePayload)
      );

      if (executeRes?.status_code === 200) {
        this.toastr.success("Withdrawal initiated successfully");
        this.closeWithdrawModal();
        this.loadProfitSnapshot(); // Refresh balance
      } else {
        throw new Error(executeRes?.message || "Withdrawal failed");
      }

    } catch (err: any) {
      console.error('Withdrawal failed:', err);
      const errorMsg = err?.error?.message || err?.message || 'Withdrawal failed';
      this.toastr.error(err?.code === 4001 ? 'Signature rejected' : errorMsg);
    } finally {
      this.isProcessing = false;
    }
  }
}
